/* Keyboard integration for the same-origin LoyaltyLion beacon and popup.
 * Preserve vendor visuals and handlers; unsupported/cross-origin frames are untouched.
 */
(() => {
  let session;
  const watchedPopupFrames = new WeakSet();
  const watchedPopupDocs = new WeakSet();
  const watchedBeaconFrames = new WeakSet();
  const watchedBeaconDocs = new WeakSet();
  let keyboardNavigation = false;
  const focusClass = 'loyaltylion-beacon-keyboard-focus';

  document.addEventListener('keydown', () => { keyboardNavigation = true; }, true);
  document.addEventListener('pointerdown', () => {
    keyboardNavigation = false;
    document.getElementById('loyaltylion-beacon-frame')?.classList.remove(focusClass);
  }, true);

  function enhanceBeacon(frame) {
    let doc;
    try { doc = frame.contentDocument; } catch { return null; }
    if (!doc?.body) return null;

    const beacon = doc.querySelector('[data-testid="beacon"]');
    if (!beacon) return null;

    let trigger = beacon.querySelector('.aape-loyalty-keyboard-trigger');
    if (!trigger) {
      const label = beacon.querySelector('[data-testid="beacon-title"]')?.textContent.trim() || 'Loyalty Points';
      const stylesId = 'aape-loyalty-keyboard-trigger-styles';
      if (!doc.getElementById(stylesId)) {
        const styles = doc.createElement('style');
        styles.id = stylesId;
        styles.textContent = `
          .aape-loyalty-keyboard-trigger:focus-visible {
            outline: 2px solid rgb(var(--color-keyboard-focus, 255, 255, 255));
            outline-offset: 2px;
            box-shadow: 0 0 0 2px rgb(var(--color-background, 255, 255, 255)), 0 0 0 6px rgb(var(--color-foreground, 0, 0, 0));
          }
        `;
        (doc.head || doc.body).appendChild(styles);
      }

      trigger = doc.createElement('button');
      trigger.type = 'button';
      trigger.className = 'aape-loyalty-keyboard-trigger';
      trigger.setAttribute('aria-label', label);
      trigger.style.cssText = 'appearance:none;position:absolute;inset:0;z-index:2;display:block;width:100%;height:100%;margin:0;padding:0;border:0;background:transparent;cursor:pointer;';
      if (doc.defaultView.getComputedStyle(beacon).position === 'static') beacon.style.position = 'relative';
      trigger.addEventListener('focus', () => {
        if (keyboardNavigation) frame.classList.add(focusClass);
      });
      trigger.addEventListener('blur', () => frame.classList.remove(focusClass));
      beacon.appendChild(trigger);
    }

    frame.title = trigger.getAttribute('aria-label') || 'Loyalty Points';
    return trigger;
  }

  function watchBeacon(frame) {
    if (!frame) return;
    const connectBeacon = () => {
      const trigger = enhanceBeacon(frame);
      if (trigger) {
        // Let Tab enter the embedded document at its real button, instead of
        // stopping first on the otherwise inert iframe browsing context.
        if (frame.hasAttribute('tabindex')) frame.removeAttribute('tabindex');
      } else if (frame.getAttribute('tabindex') !== '0') {
        frame.setAttribute('tabindex', '0');
      }

      let doc;
      try { doc = frame.contentDocument; } catch { return; }
      if (doc?.body && !watchedBeaconDocs.has(doc)) {
        watchedBeaconDocs.add(doc);
        doc.addEventListener('keydown', () => { keyboardNavigation = true; }, true);
        doc.addEventListener('pointerdown', () => {
          keyboardNavigation = false;
          frame.classList.remove(focusClass);
        }, true);
        new MutationObserver(connectBeacon).observe(doc.body, { childList: true, subtree: true });
      }
    };

    if (!watchedBeaconFrames.has(frame)) {
      watchedBeaconFrames.add(frame);
      frame.addEventListener('load', connectBeacon);
    }
    connectBeacon();
  }

  function getBeaconTrigger(frame) {
    if (!frame) return null;
    let doc;
    try { doc = frame.contentDocument; } catch { return frame; }
    return doc?.querySelector('.aape-loyalty-keyboard-trigger') || frame;
  }
  function cleanup() {
    if (!session) return;
    session.doc.removeEventListener('keydown', session.onKey);
    session.focus.deactivate();
    if (session.trigger?.isConnected) session.trigger.focus({ preventScroll: true });
    session = null;
  }
  function attach(frame) {
    let doc;
    try { doc = frame.contentDocument; } catch { return; }
    if (!doc?.body || !frame.getClientRects().length) return;
    const icon = doc.querySelector('svg:has(#icons-cross)');
    const heading = doc.querySelector('h3');
    if (!icon || !heading) return;
    if (session?.doc === doc && session.close.isConnected) return;
    cleanup();
    const close = doc.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    const style = doc.defaultView.getComputedStyle(icon);
    close.style.cssText = 'appearance:none;border:0;background:transparent;padding:0;margin:0;cursor:pointer;';
    ['position', 'top', 'right', 'bottom', 'left', 'width', 'height', 'zIndex'].forEach(key => { close.style[key] = style[key]; });
    icon.before(close);
    close.appendChild(icon);
    icon.style.cssText += ';position:static;width:100%;height:100%;display:block;';
    icon.setAttribute('aria-hidden', 'true');
    close.addEventListener('click', event => {
      if (event.target === close) icon.dispatchEvent(new doc.defaultView.MouseEvent('click', { bubbles: true }));
    });
    const focusStyle = doc.createElement('style');
    focusStyle.textContent = 'button:focus-visible,a:focus-visible{outline:2px solid currentColor;outline-offset:3px}';
    doc.head.appendChild(focusStyle);
    heading.id ||= 'AapeLoyaltyPanelHeading';
    doc.body.setAttribute('role', 'dialog');
    doc.body.setAttribute('aria-modal', 'true');
    doc.body.setAttribute('aria-labelledby', heading.id);
    frame.title = heading.textContent.trim();
    const beaconFrame = document.getElementById('loyaltylion-beacon-frame');
    const trigger = getBeaconTrigger(beaconFrame);
    if (trigger === beaconFrame) trigger?.setAttribute('tabindex', '0');
    const focus = new ThemeDialogFocus(frame, () => close.click());
    const controls = () => Array.from(doc.querySelectorAll('button,a[href],input,select,textarea,[tabindex]'))
        .filter(e => !e.disabled && e.tabIndex >= 0 && e.getClientRects().length && doc.defaultView.getComputedStyle(e).visibility !== 'hidden');
    const onKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); close.click(); }
      if (event.key === 'Tab') {
        const items = controls(); const first = items[0] || close; const last = items.at(-1) || close;
        if ((event.shiftKey && doc.activeElement === first) || (!event.shiftKey && doc.activeElement === last)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        }
      }
    };
    session = { frame, doc, close, focus, trigger, onKey };
    doc.addEventListener('keydown', onKey);
    focus.activate();
    close.focus({ preventScroll: true });
  }
  function connect() {
    if (session && (!session.frame.isConnected || !session.frame.getClientRects().length)) cleanup();
    const beacon = document.getElementById('loyaltylion-beacon-frame');
    watchBeacon(beacon);
    const frame = document.getElementById('loyaltylion-popup-frame');
    if (!frame) return;
    if (!watchedPopupFrames.has(frame)) {
      watchedPopupFrames.add(frame);
      frame.addEventListener('load', () => {
        observePopupDocument(frame);
        attach(frame);
      });
    }
    observePopupDocument(frame);
    attach(frame);
  }
  function observePopupDocument(frame) {
    let doc;
    try { doc = frame.contentDocument; } catch { return; }
    if (!doc?.body || watchedPopupDocs.has(doc)) return;

    watchedPopupDocs.add(doc);
    new MutationObserver(() => attach(frame)).observe(doc.body, { childList: true, subtree: true });
  }
  // LoyaltyLion may recreate the beacon or overwrite its tabindex after load.
  // Watch tabindex changes as well so the beacon stays in the document tab order.
  new MutationObserver(connect).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'hidden', 'tabindex']
  });
  connect();
})();